import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import EmailEditor from "react-email-editor";
import { Button } from "@/components/ui/button";
import { FloppyDisk, ArrowSquareOut, Smiley } from "@phosphor-icons/react";
import { toast } from "sonner";
import { FA_ICONS } from "@/lib/faIcons";

/**
 * Drag-and-drop email template designer (Unlayer / react-email-editor).
 *
 * Provides the Kanal text merge tags AND a custom Font Awesome icon picker
 * mounted above the canvas. Picking an icon + colour injects the rendered
 * base64 `<img>` HTML directly at the cursor position inside whichever Text
 * block currently holds the caret — no merge-tag round trip, no clipboard
 * paste, no Unlayer "HTML" block. Works for both the Template editor and
 * any future surface that mounts this component.
 *
 * Unlayer renders its design surface inside nested iframes; the text blocks
 * themselves are contentEditable elements somewhere down that tree. We walk
 * every reachable iframe, attach a `selectionchange` listener to each
 * Document, and remember the most recent caret position that landed inside
 * a contentEditable. When the user clicks an icon tile, we restore that
 * range and call `execCommand("insertHTML")` against the owning document,
 * which Unlayer's text engine picks up via its own mutation observer.
 */
const KANAL_TEXT_MERGE_TAGS = {
  recipient_name: {
    name: "Recipient name",
    value: "{{recipient_name}}",
    sample: "John Doe",
  },
  customer_number: {
    name: "Customer #",
    value: "{{customer_number}}",
    sample: "SW000123",
  },
  company_name: {
    name: "Company name",
    value: "{{company_name}}",
    sample: "Swift Logistics Co.",
  },
  tracking_number: {
    name: "Tracking #",
    value: "{{tracking_number}}",
    sample: "KN1234567890",
  },
  package_status: {
    name: "Package status",
    value: "{{package_status}}",
    sample: "In Transit",
  },
  verification_link: {
    name: "Verification link (URL)",
    value: "{{verification_link}}",
    sample: "https://your-app.com/verify-email?token=...",
  },
  verify_button: {
    name: "Verify-email button (HTML)",
    value: "{{verify_button}}",
    sample: "[ Verify my email — click button ]",
  },
};

const ICON_COLOURS = [
  { id: "dark", label: "Dark", hex: "#1F2937" },
  { id: "blue", label: "Kanal Blue", hex: "#002BF6" },
  { id: "white", label: "White", hex: "#FFFFFF" },
  { id: "amber", label: "Amber", hex: "#F59E0B" },
  { id: "green", label: "Green", hex: "#10B981" },
  { id: "red", label: "Red", hex: "#DC2626" },
];

/**
 * CSS filter strings that recolour a black/neutral icon to the target hue.
 * Pre-computed via https://codepen.io/sosuke/pen/Pjoqqp — they're long, ugly
 * and approximate, but they're the only way to recolour an already-encoded
 * base64 image without re-encoding the source SVG. Use the `none` entry to
 * disable filtering and ship the icon in whatever colour the source SVG
 * already encodes (the route taken by our clipboard-paste path).
 */
const ICON_COLOR_FILTERS = {
  none: { label: "Original (no filter)", filter: "none" },
  red: {
    label: "Red",
    filter: "invert(21%) sepia(100%) saturate(7414%) hue-rotate(359deg) brightness(94%) contrast(117%)",
  },
  blue: {
    label: "Blue",
    filter: "invert(13%) sepia(95%) saturate(7370%) hue-rotate(244deg) brightness(87%) contrast(153%)",
  },
  green: {
    label: "Green",
    filter: "invert(46%) sepia(82%) saturate(536%) hue-rotate(112deg) brightness(96%) contrast(89%)",
  },
  amber: {
    label: "Amber",
    filter: "invert(71%) sepia(63%) saturate(2864%) hue-rotate(0deg) brightness(101%) contrast(95%)",
  },
  dark: {
    label: "Dark",
    filter: "invert(13%) sepia(12%) saturate(1009%) hue-rotate(182deg) brightness(94%) contrast(89%)",
  },
  white: {
    label: "White",
    filter: "invert(100%) brightness(200%)",
  },
};

function svgWithColour(svg, hex) {
  return svg.replace(/currentColor/g, hex);
}
function b64(str) {
  return typeof window !== "undefined" && window.btoa
    ? window.btoa(unescape(encodeURIComponent(str)))
    : Buffer.from(str, "utf-8").toString("base64");
}
function makeImgTag(svg, hex, label) {
  const coloured = svgWithColour(svg, hex);
  return `<img src="data:image/svg+xml;base64,${b64(coloured)}" alt="${label}" width="16" height="16" style="vertical-align:middle;margin-right:6px;display:inline-block" />`;
}

/**
 * Build a hosted https:// URL for the FA icon in the requested colour.
 *
 * Uses Iconify's free public CDN (`api.iconify.design`), which serves a
 * recoloured single-file SVG with the `color` query param baked in — no
 * data: URI needed, no sprite sheet to bundle, no FA web-font dependency
 * (which most email clients strip).
 *
 * Brand icons (whatsapp, instagram, etc.) live under the `fa6-brands`
 * collection; everything else under `fa6-solid`. Returns a URL like
 * `https://api.iconify.design/fa6-solid/envelope.svg?color=%23002BF6&width=16&height=16`.
 */
const FA_BRAND_IDS = new Set([
  "whatsapp",
  "instagram",
  "facebook",
  "x-twitter",
  "linkedin",
  "youtube",
  "tiktok",
]);

function hostedIconUrl(iconId, hex) {
  const family = FA_BRAND_IDS.has(iconId) ? "fa6-brands" : "fa6-solid";
  return `https://api.iconify.design/${family}/${iconId}.svg?color=${encodeURIComponent(hex)}&width=16&height=16`;
}

export default function EmailDesigner({ initialDesign, onChange, onSave, height = 600 }) {
  const editorRef = useRef(null);
  // Direct handle on the Unlayer instance (editorRef.current.editor) so we
  // can call setMergeTags() outside the load callback whenever our
  // base64Icons state changes.
  const unlayerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const seededRef = useRef(false);

  const [iconPanelOpen, setIconPanelOpen] = useState(true);
  const [activeColour, setActiveColour] = useState(ICON_COLOURS[1]); // Kanal blue
  const [iconQuery, setIconQuery] = useState("");

  // 1. Base64 icon map kept in React state. Shape: { [icon.id]: dataUri }.
  //    Recomputed whenever the active colour changes (step 3's source signal).
  //    Stored as state rather than useMemo so the downstream merge-tag sync
  //    effect can depend on its identity directly.
  const [base64Icons, setBase64Icons] = useState({});

  // Active CSS filter applied to the merge-tag <img> tags. Lets us recolour
  // already-encoded base64 icons without re-encoding the SVG. Defaults to
  // the Blue preset so the Merge-Tags dropdown ships with branded icons.
  const [iconColorFilter, setIconColorFilter] = useState(ICON_COLOR_FILTERS.blue.filter);

  const mergeTagMap = useMemo(() => KANAL_TEXT_MERGE_TAGS, []);

  // 2. Save the unlayer instance to a ref so we can reach setMergeTags()
  //    from any effect, not just inside the load callback.
  const onLoad = () => {
    unlayerRef.current = editorRef.current?.editor || null;
    setReady(true);
  };

  // Seed design on first ready
  useEffect(() => {
    if (!ready || seededRef.current) return;
    const ed = editorRef.current?.editor;
    if (!ed) return;
    if (initialDesign && Object.keys(initialDesign).length > 0) {
      try {
        ed.loadDesign(initialDesign);
        seededRef.current = true;
      } catch (e) {
        console.warn("Failed to load Unlayer design", e);
      }
    } else {
      seededRef.current = true;
    }
  }, [ready, initialDesign]);

  // Recompute base64Icons whenever the active colour changes. Each FA SVG
  // is recoloured (currentColor → activeColour.hex) and base64-encoded into
  // a data:image/svg+xml URI keyed by icon.id.
  useEffect(() => {
    const map = {};
    for (const icon of FA_ICONS) {
      const coloured = icon.svg.replace(/currentColor/g, activeColour.hex);
      map[icon.id] = `data:image/svg+xml;base64,${b64(coloured)}`;
    }
    setBase64Icons(map);
  }, [activeColour]);

  // 3 + 4. Sync the base64Icons state INTO Unlayer's merge tags whenever the
  // map changes. Icons land inside a nested "Font Awesome Icons (<colour>)"
  // group so they don't pollute the top level of Unlayer's Merge Tags
  // dropdown. The text-level Kanal merge tags are preserved alongside them.
  useEffect(() => {
    const unlayer = unlayerRef.current;
    if (!unlayer || typeof unlayer.setMergeTags !== "function") return;
    if (!base64Icons || Object.keys(base64Icons).length === 0) return;

    const iconTags = {};
    FA_ICONS.forEach((icon) => {
      const stateBase64String = base64Icons[icon.id];
      if (!stateBase64String) return;
      const key = `fa_${icon.id.replace(/-/g, "_")}`;
      // 5. Merge-tag value template, exactly as requested — base64 src plus
      //    a CSS `filter` so the icon recolours when iconColorFilter changes
      //    without needing a fresh base64 round-trip.
      iconTags[key] = {
        name: icon.label,
        value: `<img src="${stateBase64String}" width="16" height="16" style="vertical-align: middle; filter: ${iconColorFilter};" />`,
        sample: icon.label,
      };
    });

    const updatedMergeTags = {
      ...KANAL_TEXT_MERGE_TAGS,
      fa_icons: {
        name: `Font Awesome Icons (${activeColour.label})`,
        mergeTags: iconTags,
      },
    };

    try {
      unlayer.setMergeTags(updatedMergeTags);
    } catch (e) {
      console.error("setMergeTags(base64Icons) failed", e);
    }
  }, [base64Icons, activeColour.label, iconColorFilter]);

  /**
   * Clipboard-based insertion.
   *
   * Unlayer's editor runs in a cross-origin iframe (`editor.unlayer.com`),
   * so we cannot reach into its contentDocument to call `execCommand` on
   * the user's caret directly. The reliable cross-origin path is to put
   * the rendered `<img>` HTML on the clipboard and let the user paste it
   * (⌘V / Ctrl+V) at their already-positioned cursor — Unlayer's text
   * engine renders the pasted HTML inline.
   *
   * Uses the modern `ClipboardItem` API (both `text/html` and `text/plain`
   * flavours) where available, with a legacy `execCommand("copy")` on a
   * hidden textarea as a fallback for older browsers / non-secure contexts.
   *
   * The `<img>` src is a hosted https:// URL (Iconify CDN) rather than a
   * data: URI — recipients' email clients render hosted images more
   * reliably and the resulting HTML is much smaller.
   */
  const insertIcon = useCallback((icon) => {
    const url = hostedIconUrl(icon.id, activeColour.hex);
    const ed = editorRef.current?.editor;
    if (!ed) { toast.error("Editor not ready"); return; }

    ed.saveDesign((design) => {
      const uid = () => Math.random().toString(36).slice(2, 10);
      const id = uid();

      // Unlayer's Lexical text-block editor strips <img> tags — inline icon
      // insertion inside a text block is not possible without an Unlayer Pro
      // licence. The reliable alternative is an `image` type content block,
      // which Unlayer renders at the specified size and lets the user drag
      // to the desired position on the canvas.
      const imageBlock = {
        id,
        type: "image",
        values: {
          containerPadding: "2px 4px",
          anchor: "",
          src: {
            url,
            width: 20,
            height: 20,
            autoWidth: false,
            maxWidth: "20px",
          },
          textAlign: "left",
          altText: icon.label,
          action: { name: "web", values: { href: "", target: "_blank" } },
          displayCondition: null,
          _meta: {
            htmlID: `u_content_image_${uid()}`,
            htmlClassNames: "u_content_image",
          },
          selectable: true, draggable: true, duplicatable: true,
          deletable: true, hideable: true,
        },
      };

      const rows = design?.body?.rows;
      if (!rows?.length) {
        toast.info("Add at least one row to the canvas first");
        return;
      }
      // Append to the first column of the first row so it's visible immediately
      const col = rows[0]?.columns?.[0];
      if (col) {
        col.contents = [...(col.contents || []), imageBlock];
        ed.loadDesign(design);
        toast.success(`"${icon.label}" icon added — drag it where you need it`);
      }
    });
  }, [activeColour]);

  const exportHtml = () =>
    new Promise((resolve) => {
      const ed = editorRef.current?.editor;
      if (!ed) return resolve(null);
      ed.exportHtml((data) => resolve(data));
    });

  const handleSave = async () => {
    try {
      const data = await exportHtml();
      if (!data) {
        toast.error("Designer not ready yet");
        return;
      }
      onSave?.({ design_json: data.design, html: data.html });
    } catch {
      toast.error("Failed to export email design");
    }
  };

  useEffect(() => {
    if (onChange) onChange({ exportHtml });
  }, [ready, onChange]);

  const filteredIcons = useMemo(() => {
    const q = iconQuery.trim().toLowerCase();
    if (!q) return FA_ICONS;
    return FA_ICONS.filter((i) =>
      i.id.toLowerCase().includes(q) ||
      i.label.toLowerCase().includes(q) ||
      i.category.toLowerCase().includes(q)
    );
  }, [iconQuery]);

  return (
    <div className="h-full border border-zinc-200 dark:border-zinc-800 rounded-md overflow-hidden flex flex-col" data-testid="email-designer">
      {/* Horizontal layout: narrow FA Icons side rail (left) → collapsible
          panel → Unlayer canvas. Mirrors Unlayer's own left rail so the
          control reads as "below Body" visually. The panel takes horizontal
          space when open and shrinks the canvas horizontally — the canvas's
          TOP section is never pushed off-screen. */}
      <div className="flex-1 min-h-0 flex">
        {/* Side rail — always visible, contains the FA Icons toggle */}
        <div
          className="shrink-0 w-14 border-r border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 flex flex-col items-center pt-3 gap-2"
          data-testid="fa-icon-rail"
        >
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setIconPanelOpen((v) => !v)}
            className={`w-11 h-14 flex flex-col items-center justify-center gap-1 rounded transition ${
              iconPanelOpen
                ? "bg-[#002BF6] text-white shadow"
                : "text-zinc-700 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-800"
            }`}
            title={iconPanelOpen ? "Hide FA Icons" : "Show FA Icons"}
            data-testid="fa-icon-panel-toggle"
            aria-pressed={iconPanelOpen}
          >
            <Smiley size={20} weight="fill" />
            <span className="text-[9px] font-semibold tracking-wider uppercase leading-none">Icons</span>
          </button>
        </div>
        {/* Icon panel — flex sibling so it takes its own space and doesn't
            overlay Unlayer's own property panel inside the iframe. */}
          {iconPanelOpen && (
            <div
              className="shrink-0 w-[260px] border-r border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 flex flex-col"
              data-testid="fa-icon-panel"
            >
              <div className="px-3 py-2 border-b border-zinc-100 dark:border-zinc-800 flex items-center flex-wrap gap-2" data-testid="fa-icon-color-row">
                <span className="text-[11px] font-semibold text-zinc-700 dark:text-zinc-200 w-full">Colour</span>
                {ICON_COLOURS.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setActiveColour(c);
                      // Keep the merge-tag filter in sync with the swatch
                      // so users get one cohesive colour pick across both
                      // the clipboard path and the Unlayer Merge-Tags path.
                      const preset = ICON_COLOR_FILTERS[c.id];
                      if (preset) setIconColorFilter(preset.filter);
                    }}
                    title={c.label}
                    className={`w-6 h-6 rounded-full transition border-2 ${
                      activeColour.id === c.id
                        ? "border-[#002BF6] scale-110 shadow"
                        : "border-zinc-300 hover:border-zinc-500"
                    }`}
                    style={{ background: c.hex }}
                    data-testid={`fa-color-${c.id}`}
                    aria-label={`${c.label} colour`}
                  />
                ))}
              </div>
              <div className="px-3 pt-2 shrink-0" data-testid="fa-icon-filter-row">
                <label className="block text-[10px] font-semibold text-zinc-600 dark:text-zinc-300 mb-1">
                  Merge-tag CSS filter (test)
                </label>
                <select
                  value={iconColorFilter}
                  onChange={(e) => setIconColorFilter(e.target.value)}
                  className="w-full mb-2 px-2 py-1 text-xs border border-zinc-200 dark:border-zinc-700 rounded bg-white dark:bg-zinc-900 dark:text-zinc-100"
                  data-testid="fa-icon-filter-select"
                >
                  {Object.entries(ICON_COLOR_FILTERS).map(([key, preset]) => (
                    <option key={key} value={preset.filter}>{preset.label}</option>
                  ))}
                </select>
              </div>
              <div className="px-3 pt-2 shrink-0">
                <input
                  type="text"
                  value={iconQuery}
                  onChange={(e) => setIconQuery(e.target.value)}
                  placeholder="Search icons (envelope, truck…)"
                  className="w-full mb-2 px-2 py-1 text-xs border border-zinc-200 dark:border-zinc-700 rounded bg-white dark:bg-zinc-900 dark:text-zinc-100"
                  data-testid="fa-icon-search"
                />
              </div>
              <div className="grid grid-cols-5 gap-1.5 pt-1 pb-3 px-3 overflow-y-auto flex-1 min-h-0">
                {filteredIcons.length === 0 ? (
                  <div className="col-span-full px-3 py-6 text-center text-xs text-zinc-500">
                    No icons match.
                  </div>
                ) : filteredIcons.map((icon) => (
                  <button
                    key={icon.id}
                    type="button"
                    onClick={() => insertIcon(icon)}
                    title={`${icon.label} (${activeColour.label})`}
                    className="aspect-square flex items-center justify-center p-1 rounded border border-transparent hover:border-[#002BF6] hover:bg-blue-50 dark:hover:bg-zinc-800 transition [&_svg]:!m-0 [&_svg]:!align-middle [&_svg]:!inline-block"
                    data-testid={`fa-icon-tile-${icon.id}`}
                    aria-label={icon.label}
                    style={{ background: activeColour.id === "white" ? "#374151" : "transparent" }}
                  >
                    <span
                      className="inline-flex items-center justify-center"
                      dangerouslySetInnerHTML={{
                        __html: icon.svg
                          .replace(/currentColor/g, activeColour.hex)
                          .replace('width="16"', 'width="20"')
                          .replace('height="16"', 'height="20"'),
                      }}
                    />
                  </button>
                ))}
              </div>
              <div className="px-3 py-2 border-t border-zinc-100 dark:border-zinc-800 text-[10px] text-zinc-500 leading-snug shrink-0">
                Click an icon to add it to your design. Drag to reposition.
              </div>
            </div>
          )}
          {/* Unlayer canvas — flex-1, shrinks when the icon panel is open */}
          <div className="flex-1 min-w-0 min-h-0">
            <EmailEditor
              ref={editorRef}
              onLoad={onLoad}
              minHeight={height}
              options={{
                mergeTags: mergeTagMap,
                features: {
                  preview: true,
                  imageEditor: true,
                },
                appearance: {
                  theme: "light",
                  panels: {
                    tools: { dock: "left" },
                  },
                },
                tools: {},
              }}
            />
          </div>
      </div>
      {!!onSave && (
        <div className="px-3 py-2 border-t border-zinc-200 bg-zinc-50 flex justify-end gap-2 dark:bg-zinc-900 dark:border-zinc-800">
          <Button
            type="button"
            size="sm"
            onClick={async () => {
              const data = await exportHtml();
              if (data?.html) {
                const w = window.open("", "_blank");
                if (w) {
                  w.document.write(data.html);
                  w.document.close();
                }
              }
            }}
            variant="outline"
            data-testid="email-designer-preview-btn"
          >
            <ArrowSquareOut size={14} className="mr-1.5" />
            Preview
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            className="bg-[#002BF6] hover:bg-[#0021C7] text-white"
            data-testid="email-designer-save-btn"
          >
            <FloppyDisk size={14} className="mr-1.5" />
            Save template
          </Button>
        </div>
      )}
    </div>
  );
}
